import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TaggingOptions, UiKitModule } from '@serviceplan/ui-kit';
import {
  AssetUIModel,
  ClientCoreModule,
  ClientEnvironment,
  ClientNotificationHandlerService,
  CommonUtilityLib,
  ENVIRONMENT,
  PROMPTS,
  ValueListsService,
  accessAsset,
  anyAsset,
  minDateValidator,
  removeTime,
} from '@thehubz/client/core';
import {
  Asset,
  AssetAspectRatio,
  AssetFileFormat,
  AssetsApiService,
  NotifyOptions,
  UserAgencyClientBrandModel,
  UserAgencyClientModel,
  UserAgencyModel,
  UserInfo,
  UsersTaggingApiService,
} from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';
import { AssetNotificationsComponent } from '../../../asset-notifications/asset-notifications.component';
import { AssetMetadata } from '../../models/asset-info.interface';
import { AssetCommonService } from './../../../../../services/asset-common.service';
import { ArvalAssetMetadataComponent } from './custom-clients/arval/asset-metadata/arval-asset-metadata.component';
import { LufthansaAssetMetadataComponent } from './custom-clients/lufthansa/lufthansa-metadata/lufthansa-asset-metadata.component';
import { SeatAssetMetadataComponent } from './custom-clients/seat/seat-metadata/seat-asset-metadata.component';
import { VKBAssetMetadataComponent } from './custom-clients/vkb/vkb-metadata/vkb-asset-metadata.component';
import { VorwerkAssetMetadataComponent } from './custom-clients/vorwerk/asset-metadata/vorwerk-asset-metadata.component';
import { WestlottoMetadataComponent } from './custom-clients/westlotto/westlotto-metadata/westlotto-metadata.component';

@Component({
  selector: 'thehubz-asset-metadata',
  templateUrl: './asset-metadata.component.html',
  styleUrls: ['./asset-metadata.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    ClientCoreModule,
    UiKitModule,
    AssetNotificationsComponent,
    ArvalAssetMetadataComponent,
    LufthansaAssetMetadataComponent,
    VKBAssetMetadataComponent,
    SeatAssetMetadataComponent,
    VorwerkAssetMetadataComponent,
    WestlottoMetadataComponent,
  ],
})
export class AssetMetadataComponent implements OnInit, OnChanges {
  @Input() asset!: AssetUIModel;
  @Input() assetInfoObject!: AssetMetadata;

  @Output() stateChange = new EventEmitter<AnyType>();

  @ViewChild('arvalAssetMetadata') arvalAssetMetadata!: ArvalAssetMetadataComponent;

  readonly brandingKey = this.env.brandingKey;
  readonly CustomClients = CustomClients;

  editFlag = false;
  updateAssetForm: UntypedFormGroup;

  agencies!: UserAgencyModel[];
  companies!: UserAgencyClientModel[];
  brands!: UserAgencyClientBrandModel[];

  mediumFields: string[] = [];
  agencySelected!: { name: string; _id: string };
  clientSelected!: { name: string; _id: string };
  brandSelected!: { name: string; _id: string };
  todayDate = new Date();
  AccessAsset = accessAsset;
  AnyAsset = anyAsset;
  taggingOptions: TaggingOptions = {
    roles: ['global.library', 'asset.update'],
  };
  taggedUser!: UserInfo[]; // tagged user list
  descriptionText!: string; // sp-editor description text

  minPublishDate = new Date();

  textIsRequired = PROMPTS.TEXT.IS_REQUIRED;

  constructor(
    @Inject(ENVIRONMENT) private env: ClientEnvironment,
    private assetsService: AssetsApiService,
    private _formBuilder: UntypedFormBuilder,
    private commonUtilityLib: CommonUtilityLib,
    protected valueListsService: ValueListsService,
    protected assetCommonService: AssetCommonService,
    private usersTaggingApiService: UsersTaggingApiService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
  ) {
    this.updateAssetForm = this._formBuilder.group({
      name: ['Initiate', Validators.required],
      agency: ['Check', Validators.required],
      client: ['Check', Validators.required],
      brand: ['Check', Validators.required],
      description: '',
      status: ['Check', Validators.required],
      jobNumber: '',
      plannedDeletion: '',
      publishStartDate: '',
      publishEndDate: '',
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['asset']?.currentValue) {
      this.asset = changes['asset'].currentValue;
    }
    if (changes['assetInfoObject']?.currentValue) {
      this.assetInfoObject = changes['assetInfoObject'].currentValue;
      this.getAgencies();
    }
  }

  ngOnInit(): void {
    if (this.asset) {
      this.asset.assetExpiryDays = this.assetCommonService.getAssetExpireDays(this.asset?.expiryDate);
      this.assetInfoObject?.metadata?.forEach((obj) => {
        if (Object.keys(this.updateAssetForm.value).indexOf(obj.formControlName) !== -1) {
          if (obj.editable) {
            this.updateAssetForm.controls[obj.formControlName].enable();
          } else {
            this.updateAssetForm.controls[obj.formControlName].disable();
          }
        }
      });
    }
    if (this.brandingKey != CustomClients.LufthansaMediacenter && this.brandingKey != CustomClients.LufthansaMedialounge) {
      this.setMediumData();
    }
  }

  private setMediumData() {
    this.mediumFields = [];
    if (this.asset?.currentVersion && this.asset?.currentVersion?.aspectRatios && this.asset?.currentVersion?.aspectRatios?.length > 0) {
      this.asset.currentVersion.aspectRatios.forEach((aspectRatio: AssetAspectRatio) => {
        aspectRatio.formats?.forEach((fileFormat: AssetFileFormat) => {
          if (fileFormat.assetMedium && !this.mediumFields.includes(' ' + fileFormat.assetMedium)) {
            this.mediumFields.push(' ' + fileFormat.assetMedium);
          }
        });
      });
    }
  }

  private patchFormValues() {
    this.assetCommonService.patchFormValues(this.asset, this.updateAssetForm);
    const publishStartDate = removeTime(new Date(this.asset.publishStartDate || new Date()));
    const today = removeTime(new Date());
    if (publishStartDate < today) {
      this.minPublishDate = publishStartDate;
    } else {
      this.minPublishDate = today;
    }

    const publishStartDateControl = this.updateAssetForm.get('publishStartDate') as FormControl;
    const publishEndDateControl = this.updateAssetForm.get('publishEndDate') as FormControl;
    this.updateAssetForm.setValidators(publishDateValidator(publishStartDateControl, publishEndDateControl, this.minPublishDate));
  }

  /**
   * Update asset property on save
   */
  saveAssetInfo() {
    switch (this.assetInfoObject.customerName) {
      case CustomClients.Standard:
      case CustomClients.LufthansaMediacenter:
      case CustomClients.LufthansaMedialounge:
        this.handleStandardClient();
        break;
      case CustomClients.Arval:
        this.handleArvalClient();
        break;
      default:
        break;
    }
    this.editFlag = !this.editFlag;
    if (this.brandingKey === CustomClients.LufthansaMediacenter || this.brandingKey === CustomClients.LufthansaMedialounge) {
      this.stateChange.emit(false);
    }
  }

  private handleStandardClient() {
    if (this.updateAssetForm.valid) {
      const formValues = this.extractStandardClientFormValues();
      this.updateAssetInfo(formValues);
    }
  }

  private extractStandardClientFormValues() {
    const formValues = this.updateAssetForm.getRawValue();
    this.deleteFormValuesProperties(formValues, ['createdBy', 'updatedBy', 'updatedAt', 'createdAt']);
    formValues.status = formValues.status?.id;
    if (this.brandingKey === CustomClients.LufthansaMediacenter || this.brandingKey === CustomClients.LufthansaMedialounge) {
      this.deleteFormValuesProperties(formValues, ['agency', 'client', 'plannedDeletion']);
    }
    formValues.brand = this.brandSelected;
    return formValues;
  }

  private handleArvalClient() {
    if (this.arvalAssetMetadata?.arvalMetadataForm.valid) {
      const formValues = this.extractArvalClientFormValues();
      this.updateAssetInfo(formValues);
    }
  }

  private extractArvalClientFormValues() {
    const formValues: AnyType = {};
    formValues['customFields'] = {};
    const arvalMetadata = this.arvalAssetMetadata.arvalMetadataForm.getRawValue();
    formValues.name = arvalMetadata.name;
    formValues.status = arvalMetadata.status?.id;
    formValues.description = arvalMetadata.description;
    this.deleteFormValuesProperties(arvalMetadata, ['name', 'status', 'description']);
    Object.keys(arvalMetadata).forEach((key) => {
      if (arvalMetadata[key]) formValues['customFields'][key] = arvalMetadata[key];
    });
    return formValues;
  }

  deleteFormValuesProperties(formValues: AnyType, keyArray: string[]) {
    keyArray.forEach((key) => {
      delete formValues[key];
    });
  }

  editInformation() {
    this.editFlag = !this.editFlag;
    switch (this.assetInfoObject.customerName) {
      case CustomClients.Arval:
        this.arvalAssetMetadata.patchFormValues();
        break;
      default:
        this.patchFormValues();
        break;
    }
    if (this.brandingKey === CustomClients.LufthansaMediacenter || this.brandingKey === CustomClients.LufthansaMedialounge) {
      if (this.editFlag) {
        this.stateChange.emit(true);
      } else {
        this.stateChange.emit(false);
      }
    }
    this.agencySelected = { name: this.asset.agency?.name, _id: this.asset.agency?._id.toString() };
    this.clientSelected = { name: this.asset.client?.name, _id: this.asset.client?._id.toString() };
    this.brandSelected = { name: this.asset.brand?.name, _id: this.asset.brand?._id.toString() };
    if (!this.editFlag && this.updateAssetForm.dirty) {
      this.resetClients();
      this.resetBrands();
    }
  }

  resetClients() {
    const agency = this.agencies.find((agency) => agency._id === this.asset.agency._id.toString());
    if (agency && agency.clients?.length) {
      this.companies = agency.clients;
      this.assetInfoObjectUpdates('client', this.companies);
    }
  }

  resetBrands() {
    const client = this.companies.find((company) => company._id === this.asset.client._id.toString());
    if (client && client.brands?.length) {
      this.brands = client.brands;
      this.assetInfoObjectUpdates('brand', this.brands);
    }
  }

  /**
   * store tagged user list when user tagged get at sp-editor
   * @param userList
   */
  taggedUserList(userList: UserInfo[]) {
    this.taggedUser = userList;
  }

  /**
   * store description text from sp-editor change
   * @param descriptionText
   */
  editorDescriptionText(descriptionText: string) {
    this.descriptionText = descriptionText;
  }

  updateAssetInfo(formData: AnyType) {
    this.assetsService.assetsControllerUpdate(this.asset?._id, formData).subscribe({
      next: (data: Asset) => {
        if (data) {
          // notify users tagged in sp-editor component
          if (this.taggedUser && this.taggedUser.length) {
            const notifyOptions: NotifyOptions = {
              userInfo: this.taggedUser,
              callFromContext: '',
              contextPath: 'asset',
              contextId: data._id,
              contextName: data.name,
              editorDescription: this.descriptionText,
            };
            this.usersTaggingApiService.usersTaggingControllerNotifyTaggedUsers(notifyOptions).subscribe();
          }
          // deleting currentVersion from data (object returned from update)
          // as update doesn't populate currentVersion
          delete data.currentVersion;
          this.asset = { ...this.asset, ...data };
          this.asset.assetExpiryDays = this.assetCommonService.getAssetExpireDays(this.asset?.expiryDate);
          // emit the latest change
          this.assetCommonService.assetDataSubject$.next(this.asset);
          this.commonUtilityLib.emitNewHeaderline(data.name);
          this.patchFormValues();
          if (this.arvalAssetMetadata && this.assetInfoObject.customerName === CustomClients.Arval) {
            this.arvalAssetMetadata.patchFormValues();
          }
          this.clientNotificationHandlerService.successNotification(`${data.name} successfully updated`);
        }
      },
      error: (err) => {
        this.clientNotificationHandlerService.errorNotification(err);
      },
    });
  }

  assetInfoObjectUpdates(key: string, optionValues: AnyType) {
    if (this.assetInfoObject) {
      const index = this.assetInfoObject.metadata.findIndex((obj) => obj.formControlName === key);
      if (index !== -1) this.assetInfoObject.metadata[index].options = optionValues;
    }
  }

  /* Getting all the companies from db */
  getAgencies(): void {
    this.valueListsService.listUserAgencies(['asset.update']).subscribe((response) => {
      if (response && response.length > 0) {
        //Set Agency options
        this.agencies = response;
        const agencyIndex = this.agencies.findIndex((agency) => agency._id === this.asset?.agency?._id.toString());
        if (agencyIndex !== -1) {
          this.assetInfoObjectUpdates('agency', this.agencies);
          //Set Client options
          this.companies = this.agencies[agencyIndex].clients;
          const clientIndex = this.companies.findIndex((company) => company._id === this.asset?.client?._id.toString());
          if (clientIndex !== -1) {
            this.assetInfoObjectUpdates('client', this.companies);
            //Set brand options
            this.brands = this.companies[clientIndex].brands;
            this.assetInfoObjectUpdates('brand', this.brands);
          }
        } else {
          this.assetInfoObjectUpdates('agency', this.agencies);
        }
      }
    });
  }

  onAgencySelect(event: AnyType, formType: string): void {
    switch (formType) {
      case 'agency': {
        const agency = this.agencies.find((agency: AnyType) => agency._id === event.value._id);
        if (agency) this.agencySelected = { name: agency.name, _id: agency._id };
        if (agency && agency.clients?.length) {
          this.companies = agency.clients;
        } else {
          this.companies = [];
          this.brands = [];
        }
        this.assetInfoObjectUpdates('client', this.companies);
        this.assetInfoObjectUpdates('brand', this.brands);
        if (!event.source.singleValue) {
          this.updateAssetForm.controls['client'].setValue(null);
          this.updateAssetForm.controls['brand'].setValue(null);
        }
        this.updateAssetForm.controls['client'].markAsTouched();
        this.updateAssetForm.controls['client'].updateValueAndValidity();
        break;
      }
      case 'client': {
        const client = this.companies.find((company: AnyType) => company._id === event.value._id);
        if (client) this.clientSelected = { name: client.name, _id: client._id };
        if (client && client.brands?.length) {
          this.brands = client.brands;
        } else {
          this.brands = [];
        }
        this.assetInfoObjectUpdates('brand', this.brands);
        if (!event.source.singleValue) {
          this.updateAssetForm.controls['brand'].setValue(null);
        }
        this.updateAssetForm.controls['brand'].markAsTouched();
        this.updateAssetForm.controls['brand'].updateValueAndValidity();
        break;
      }
      case 'brand': {
        const brand = this.brands.find((brnd: AnyType) => brnd._id === event.value._id);
        if (brand) this.brandSelected = { name: brand.name, _id: brand._id };
        break;
      }
    }
  }

  onDateSelection(formControlName: string) {
    const publishDateControl = this.updateAssetForm.get(formControlName) as FormControl;
    if (publishDateControl.value) {
      publishDateControl.setValidators(minDateValidator(publishDateControl));
      publishDateControl.updateValueAndValidity();
    }
  }

  restrictInput(event: KeyboardEvent): void {
    const allowedPattern = /^[0-9-]$/; // Allow only numbers and hyphen
    if (!allowedPattern.test(event.key)) {
      event.preventDefault();
    }
  }
}

/**
 * Custom validator function to validate publish start and end dates.
 * startDate cannot be less the minPublishDate (if provided) or today's date.
 * endDate cannot be less than startDate.
 * @param startDateControl The control for the publish start date.
 * @param endDateControl The control for the publish end date.
 * @returns A validator function that returns an object if the validation fails, or null otherwise.
 */
export function publishDateValidator(startDateControl: AbstractControl, endDateControl: AbstractControl, minPublishDate: Date): ValidatorFn {
  return (control: AbstractControl): Record<string, any> | null => {
    const startDate = startDateControl.value;
    const endDate = endDateControl.value;
    if (!startDate || !endDate) {
      return null;
    }
    const date = removeTime(new Date());
    const startDateValue = removeTime(startDate);
    const endDateValue = removeTime(endDate);
    if (minPublishDate) {
      const minPublishDateValue = removeTime(minPublishDate);
      if (startDateValue < minPublishDateValue) {
        return { startDateInvalid: true };
      }
    } else {
      if (startDateValue < date) {
        return { startDateInvalid: true };
      }
    }
    if (endDateValue < startDateValue) {
      return { endDateInvalid: true };
    }
    return null;
  };
}
